import { useRef, useEffect, memo, useState, UIEvent } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useSendMessageMutation } from 'common/api/message';
import { handleError } from 'helpers/sentry';
import { markCandidateMessagesAsReadInFirebase, updateAlvyMessageStatus } from 'helpers/messages';
import { isSessionEnded } from 'helpers/session';
import { Message, Messages } from 'globals/interfaces';
import ChatMessages from './ChatMessages';
import BundledEditor from 'components/editor/Editor';
import { TINYMCE_LICENSE_KEY } from 'common/components/constant';
import { AlvyMessageStatus, ChatMessageRole } from 'globals/enums';
import { Mixpanel } from '../../Mixpanel';

interface Props {
  messages: Messages;
  session_id: string;
  session_uuid: string;
  participant_group_id?: number;
  session_status: string;
  searchQuery?: string;
  searchedMessages?: Message[];
}

const ChatWindow = memo(({ messages, session_id, session_uuid, participant_group_id, session_status }: Props) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [sendMessage, { isLoading: isSendingMessage }] = useSendMessageMutation();
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);

  const isSessionOver = isSessionEnded(session_status);
  const chatMessages = messages
    ? Object.keys(messages)
        .map((key) => ({ ...messages[key], key }))
        .filter((message) => message.role !== ChatMessageRole.System)
    : [];

  const handleSendMessage = async () => {
    const inputText = editorRef.current?.getContent();
    if (inputText?.trim() !== '') {
      try {
        const messageData = {
          role: ChatMessageRole.Proctor,
          role_to: [ChatMessageRole.Candidate, ChatMessageRole.Alvy],
          text: inputText,
          session_id: Number(session_id),
        };
        editorRef.current.setContent('');
        setIsPinnedToBottom(true);
        await sendMessage(messageData).unwrap();
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleEditMessage = async (messageText: string, session_id: number, messageKey: string) => {
    try {
      editorRef.current.setContent(messageText);
      await updateAlvyMessageStatus(session_id, messageKey, AlvyMessageStatus.Updated, participant_group_id);
      Mixpanel.track('proctoring_message_edited');
    } catch (error) {
      handleError(error);
    }
  };

  const handleRejectMessage = async (session_id: number, messageKey: string) => {
    try {
      await updateAlvyMessageStatus(session_id, messageKey, AlvyMessageStatus.Rejected, participant_group_id);
      Mixpanel.track('proctoring_message_rejected');
    } catch (error) {
      handleError(error);
    }
  };

  const messageCount = chatMessages?.length || 0;

  useEffect(() => {
    if (!messages || !messagesEndRef.current) return;
    const raf = window.requestAnimationFrame(() => {
      markCandidateMessagesAsReadInFirebase(session_id, messages, participant_group_id);
      if (isPinnedToBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    });
    return () => window.cancelAnimationFrame(raf);
  }, [session_id, participant_group_id, messages, messageCount, isPinnedToBottom]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const isAtBottom = target.scrollHeight - (target.scrollTop + target.clientHeight) <= 20;
    setIsPinnedToBottom(isAtBottom);
  };

  const editorRef = useRef(null);

  return (
    <>
      <div
        className="flex flex-col flex-grow overflow-y-auto"
        style={{ maxHeight: 'calc(100%)' }}
        data-testid="chat-scroll-container"
        onScroll={handleScroll}
      >
        <ChatMessages
          session_id={Number(session_id)}
          session_uuid={session_uuid}
          participant_group_id={participant_group_id}
          chatMessages={chatMessages}
          handleEditMessage={handleEditMessage}
          handleRejectMessage={handleRejectMessage}
        />
        <div data-testid="messages-end-ref" ref={messagesEndRef}></div>
      </div>
      <div className="flex items-center justify-center gap-1">
        <BundledEditor
          onInit={(evt, editor) => (editorRef.current = editor)}
          init={{
            height: 300,
            menubar: false,
            plugins: ['link', 'lists', 'autolink'],
            toolbar:
              'bold italic underline| \
            bullist numlist',
            license_key: TINYMCE_LICENSE_KEY,
          }}
          key={session_id}
          content_style="text-gray-600"
          handleEnter={handleSendMessage}
          disabled={isSessionOver || isSendingMessage}
        />
        <button
          data-testid="send-message"
          disabled={isSessionOver}
          className="h-full flex items-center bg-white border px-2.5 rounded-md"
        >
          <PaperAirplaneIcon
            className={`w-5 h-5 cursor-pointer ${
              isSessionOver ? 'fill-gray-300' : 'text-blue-700'
            }`}
            onClick={handleSendMessage}
          />
        </button>
      </div>
    </>
  );
});

export default ChatWindow;
